/**
 * FlahaSOIL v2 — Salinity and Sodicity chart adapter (Phase 10C-D).
 */
import type {
  SoilInterpretationDTO,
  SoilChemistryResultDTO,
  SoilPhysicsResultDTO,
  SoilChemistryInputDTO,
  SoilTextureInputDTO
} from "@flaha/shared-types";
import type { ScientificChartData, ScientificChartStatus } from "../types/ScientificChartTypes";
import { CHART_UNITS } from "../utils/chartUnits";

export interface SalinitySodicityChartData {
  ec: number | null;
  sar: number | null;
  esp: number | null;
  clay: number | null;
  salinitySeverity: string | null;
  sodicitySeverity: string | null;
}

export function salinitySodicityChartAdapter(
  interpretation: SoilInterpretationDTO | null,
  chemistryResult: SoilChemistryResultDTO | null,
  physicsResult: SoilPhysicsResultDTO | null,
  chemistryInput?: SoilChemistryInputDTO | null,
  textureInput?: SoilTextureInputDTO | null
): ScientificChartData<SalinitySodicityChartData> {
  let status: ScientificChartStatus = "READY";
  const warnings: string[] = [];

  const ec = chemistryInput?.ecDsM ?? null;
  const sar = chemistryResult?.sar ?? null;
  const esp = chemistryResult?.esp ?? null;
  const clay = textureInput?.clayPercent ?? null;

  const data: SalinitySodicityChartData = {
    ec,
    sar,
    esp,
    clay,
    salinitySeverity: interpretation?.salinitySeverity ?? null,
    sodicitySeverity: interpretation?.sodicitySeverity ?? null,
  };

  if (!interpretation) {
    status = "MISSING_DATA";
  } else if (ec === null && sar === null && esp === null) {
    status = "PARTIAL_DATA";
    warnings.push("Salinity/Sodicity inputs missing — quantitative hazard charts unavailable.");
  }

  return {
    status,
    title: "Salinity & Sodicity Diagnostics",
    description: "Assessment of soil salinity (EC) and sodicity (SAR/ESP) hazards.",
    data,
    warnings,
    units: {
      ec: CHART_UNITS.ec,
      esp: CHART_UNITS.percent,
      sar: CHART_UNITS.unitless,
      clay: CHART_UNITS.percent,
    },
    source: "FAO-29 / FlahaSOIL Interpretation",
    accessibilitySummary: `Salinity severity is ${data.salinitySeverity ?? "unknown"}. Sodicity severity is ${data.sodicitySeverity ?? "unknown"}.`,
  };
}
